import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { MapPin, Calendar, User, Mail, Phone, Truck, FileText, DollarSign, CheckCircle2, PhoneCall, AlertCircle } from 'lucide-react';
// =====================================================
// EMAILJS CONFIGURATION
// Replace these placeholder values with your real EmailJS credentials.
// 1. Go to https://www.emailjs.com → create an account
// 2. Add an Email Service (Gmail, Outlook, etc.) → copy the Service ID
// 3. Create an Email Template → copy the Template ID
// 4. From Account → API Keys → copy the Public Key
// =====================================================
const EMAILJS_SERVICE_ID = 'YOUR_SERVICE_ID';
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID';
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY';
type FormState = 'idle' | 'sending' | 'success' | 'error';
export function BookingForm() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].booking;
  const [minDate, setMinDate] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const date = new Date();
    date.setDate(date.getDate() + 3);
    setMinDate(date.toISOString().split('T')[0]);
  }, []);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;
    setFormState('sending');
    try {
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, {
        publicKey: EMAILJS_PUBLIC_KEY
      });
      setFormState('success');
      formRef.current.reset();
      // Scroll to top of section to show success message
      document.getElementById('booking')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } catch (err) {
      console.error('EmailJS error:', err);
      setFormState('error');
    }
  };
  const resetForm = () => setFormState('idle');
  return <section id="booking" className="py-24 bg-mf-gray relative overflow-hidden" data-id="element-18">
      {/* Decorative top wave */}
      <div className="absolute top-0 left-0 w-full h-48 bg-mf-black transform -skew-y-2 origin-top-left z-0" data-id="element-19"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-20">
        <AnimatePresence mode="wait" data-id="element-21">
          {formState === 'success' ? <SuccessMessage key="success" text={text.success} onReset={resetForm} backText={text.success.cta} data-id="element-22" /> : <motion.div key="form" initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20
        }} exit={{
          opacity: 0,
          y: -20
        }} className="bg-white rounded-3xl shadow-2xl overflow-hidden border-t-8 border-mf-red" data-id="element-23">
              <div className="bg-mf-black text-white py-8 px-8 text-center relative overflow-hidden" data-id="element-24">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[150px] bg-mf-red/20 blur-[80px] rounded-full pointer-events-none" data-id="element-25"></div>
                <h2 className="text-3xl md:text-5xl font-condensed font-black uppercase tracking-tight flex items-center justify-center relative z-10" data-id="element-26">
                  <Truck className="mr-3 text-mf-yellow" size={40} data-id="element-27" />
                  {text.title}
                </h2>
                <p className="mt-3 text-gray-300 max-w-2xl mx-auto relative z-10 text-sm md:text-base" data-id="element-28">
                  {text.subtitle}
                </p>
              </div>

              <div className="p-6 md:p-10" data-id="element-29">
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-30">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-31">
                    {/* Move Size */}
                    <FormField label={text.sizeLabel} fullWidth data-id="element-32">
                      <SelectInput name="size" required options={text.sizeOptions} placeholder={text.sizeLabel} data-id="element-33" />
                    </FormField>

                    {/* Service Type */}
                    <FormField label={text.serviceTypeLabel} fullWidth icon={<DollarSign className="h-5 w-5 text-gray-400" data-id="element-35" />} data-id="element-34">
                      <SelectInput name="service_type" required options={text.serviceTypeOptions} placeholder={text.serviceTypeLabel} data-id="element-36" />
                    </FormField>

                    {/* Origin */}
                    <FormField label={text.originLabel} data-id="element-37">
                      <TextInput name="origin" type="text" placeholder="123 Main St, Tampa, FL" required icon={<MapPin className="h-5 w-5 text-gray-400" data-id="element-39" />} data-id="element-38" />
                    </FormField>

                    {/* Destination */}
                    <FormField label={text.destLabel} data-id="element-40">
                      <TextInput name="destination" type="text" placeholder="456 Beach Rd, Miami, FL" required icon={<MapPin className="h-5 w-5 text-mf-red" data-id="element-42" />} data-id="element-41" />
                    </FormField>

                    {/* Date */}
                    <FormField label={text.dateLabel} hint={text.dateHint} fullWidth data-id="element-43">
                      <TextInput name="date" type="date" required min={minDate} icon={<Calendar className="h-5 w-5 text-gray-400" data-id="element-45" />} data-id="element-44" />
                    </FormField>

                    {/* Name */}
                    <FormField label={text.nameLabel} data-id="element-46">
                      <TextInput name="name" type="text" required icon={<User className="h-5 w-5 text-gray-400" data-id="element-48" />} data-id="element-47" />
                    </FormField>

                    {/* Phone */}
                    <FormField label={text.phoneLabel} data-id="element-49">
                      <TextInput name="phone" type="tel" required placeholder="(813) 476-8935" icon={<Phone className="h-5 w-5 text-gray-400" data-id="element-51" />} data-id="element-50" />
                    </FormField>

                    {/* Email */}
                    <FormField label={text.emailLabel} fullWidth data-id="element-52">
                      <TextInput name="email" type="email" required icon={<Mail className="h-5 w-5 text-gray-400" data-id="element-54" />} data-id="element-53" />
                    </FormField>

                    {/* Notes */}
                    <FormField label={text.notesLabel} fullWidth data-id="element-55">
                      <div className="relative" data-id="element-56">
                        <div className="absolute top-3 left-3 pointer-events-none" data-id="element-57">
                          <FileText className="h-5 w-5 text-gray-400" data-id="element-58" />
                        </div>
                        <textarea name="notes" rows={3} placeholder={text.notesPlaceholder} className="block w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-mf-red bg-gray-50 transition-colors resize-none" data-id="element-59" />
                      </div>
                    </FormField>
                  </div>

                  {formState === 'error' && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start" data-id="element-60">
                      <AlertCircle className="text-mf-red mr-3 flex-shrink-0 mt-0.5" size={20} data-id="element-61" />
                      <p className="text-mf-red text-sm font-medium" data-id="element-62">
                        {text.error}
                      </p>
                    </motion.div>}

                  <div className="pt-4" data-id="element-63">
                    <motion.button type="submit" disabled={formState === 'sending'} whileHover={{
                  scale: formState === 'sending' ? 1 : 1.02
                }} whileTap={{
                  scale: formState === 'sending' ? 1 : 0.98
                }} className="w-full flex justify-center items-center py-5 px-4 rounded-xl shadow-lg text-xl font-condensed font-black bg-mf-red hover:bg-red-700 text-white uppercase tracking-widest transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-64">
                      {formState === 'sending' ? <>
                          <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-65">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-66"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-67"></path>
                          </svg>
                          {text.submitting}
                        </> : text.submit}
                    </motion.button>
                    <p className="text-center text-xs text-gray-500 mt-4 font-medium" data-id="element-68">
                      {text.footer}
                    </p>
                  </div>
                </form>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}
// ----- Sub-components -----
function FormField({
  label,
  hint,
  fullWidth,
  children
}: {
  label: string;
  hint?: string;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return <div className={fullWidth ? 'col-span-1 md:col-span-2' : 'col-span-1'} data-id="element-69">
      <label className="block text-sm font-bold text-mf-darkgray mb-2 uppercase tracking-wide" data-id="element-70">
        {label}
        {hint && <span className="text-xs text-mf-red font-normal normal-case ml-2" data-id="element-71">
            ({hint})
          </span>}
      </label>
      {children}
    </div>;
}
function TextInput({
  name,
  type,
  placeholder,
  required,
  min,
  icon
}: {
  name: string;
  type: string;
  placeholder?: string;
  required?: boolean;
  min?: string;
  icon?: React.ReactNode;
}) {
  return <div className="relative" data-id="element-72">
      {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-73">
          {icon}
        </div>}
      <input type={type} name={name} placeholder={placeholder} required={required} min={min} className={`block w-full ${icon ? 'pl-10' : 'pl-3'} pr-3 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-mf-red bg-gray-50 transition-colors`} data-id="element-74" />
    </div>;
}
function SelectInput({
  name,
  required,
  options,
  placeholder
}: {
  name: string;
  required?: boolean;
  options: {
    value: string;
    label: string;
  }[];
  placeholder: string;
}) {
  return <div className="relative" data-id="element-75">
      <select name={name} required={required} defaultValue="" className="block w-full pl-4 pr-10 py-3 text-base border-2 border-gray-200 focus:outline-none focus:border-mf-red rounded-xl appearance-none bg-gray-50 font-medium transition-colors cursor-pointer" data-id="element-76">
        <option value="" disabled data-id="element-77">
          -- {placeholder} --
        </option>
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-78">
            {opt.label}
          </option>)}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-79">
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-80">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-81" />
        </svg>
      </div>
    </div>;
}
function SuccessMessage({
  text,
  onReset,
  backText
}: {
  text: any;
  onReset: () => void;
  backText: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} transition={{
    type: 'spring',
    stiffness: 100,
    damping: 15
  }} className="bg-white rounded-3xl shadow-2xl overflow-hidden border-t-8 border-green-500" data-id="element-82">
      <div className="p-10 md:p-16 text-center" data-id="element-83">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 15,
        delay: 0.2
      }} className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 text-green-600 mb-6" data-id="element-84">
          <CheckCircle2 size={56} strokeWidth={2} data-id="element-85" />
        </motion.div>

        <h3 className="text-4xl md:text-5xl font-condensed font-black text-mf-black uppercase tracking-tight mb-6" data-id="element-86">
          {text.title}
        </h3>

        <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed mb-2" data-id="element-87">
          {text.message}
        </p>

        <a href={`tel:+1${text.phone.replace(/[^\d]/g, '')}`} className="inline-flex items-center justify-center my-4 px-6 py-3 rounded-xl bg-mf-red text-white font-condensed font-black text-2xl tracking-wider hover:bg-red-700 transition-colors shadow-lg" data-id="element-88">
          <PhoneCall className="mr-3" size={28} data-id="element-89" />
          {text.phone}
        </a>

        <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed mt-2" data-id="element-90">
          {text.message2}
        </p>

        <button onClick={onReset} className="mt-10 text-sm font-bold text-mf-darkgray hover:text-mf-red uppercase tracking-wider transition-colors" data-id="element-91">
          ← {backText}
        </button>
      </div>
    </motion.div>;
}